import { z } from 'zod';
/**
 * Ações que o Secretário faz pela pessoa (estudo "Secretário faz tudo", F0). O que muda dado
 * não roda na primeira chamada: o servidor guarda a ação pendente com o resumo e um prazo, e
 * só o sim da pessoa executa (o toque em Sim na tela, ou a própria fala dela dizendo sim). O
 * modelo não consegue se confirmar sozinho, e o que roda é o que ficou guardado.
 */
export const ACTION_RISKS = ['muda', 'alto'];
/** Dinheiro a partir disto (centavos) só com o toque em Sim (estudo: "dinheiro acima de um valor"). */
export const HIGH_MONEY_CENTS = 500_000;
/** Mensagem para mais pessoas que isto só com o toque ("mensagem para muitos"). */
export const HIGH_RECIPIENTS = 3;
/** Quanto tempo a pergunta fica valendo. */
export const PENDING_ACTION_MINUTES = 5;
export const pendingActionSchema = z.object({
    id: z.string(),
    tool: z.string(),
    /** A pergunta, como a pessoa lê: "Marcar retorno da Ana Lima na quinta às 15h?" */
    summary: z.string(),
    risk: z.enum(ACTION_RISKS),
    /** O rótulo do botão do sim ("Marcar", "Dar baixa"). */
    yes: z.string(),
    expiresAt: z.iso.datetime(),
});
/**
 * O roteiro do modo Assistir (F1): depois que a ação foi feita no servidor, o aparelho repete
 * na tela o que aconteceu: abre a página, mostra a folha com os campos sendo preenchidos, o
 * botão apertado e acende o item novo. É só a tela repetindo; o dado já está gravado.
 */
export const watchScriptSchema = z.object({
    /** A tela onde a ação acontece (sempre do app, nunca Ajustes, Plano ou a conta). */
    href: z.string().startsWith('/'),
    /** O título da folha ("Marcar consulta", "Dar baixa"). */
    title: z.string().max(60),
    fields: z
        .array(z.object({ name: z.string().max(60), label: z.string().max(40), value: z.string().max(200) }))
        .max(6),
    /** O botão que a folha aperta no fim. */
    press: z.string().max(30),
    /** O item que acende na página depois (consulta, bloqueio, lançamento), se houver. */
    item: z.string().max(64).nullable(),
    /** Ou o horário que acende, na agenda do celular ("08:00"). */
    slot: z.string().max(5).nullable(),
});
/** Telas que o Assistir pode abrir: só as do trabalho (clínica, agenda, ficha, funil, financeiro). */
export function watchHrefAllowed(href) {
    return (/^\/(negocio\/(clinica|loja)|agenda|lembretes|habilidade\/contas|entregas|perto|pedidos|envios)(\/|\?|$)/.test(href) && !/\/(ajustes|plano|conta|dono)(\/|\?|$)/.test(href));
}
export const actionDecisionSchema = z.object({ decision: z.enum(['sim', 'nao']) });
export const ACTION_CATALOG = {
    marcar_consulta: { yes: 'Marcar', risk: 'muda', domain: 'clinica' },
    remarcar: { yes: 'Remarcar', risk: 'muda', domain: 'clinica' },
    cancelar_consulta: { yes: 'Desmarcar', risk: 'muda', domain: 'clinica' },
    bloquear_horario: { yes: 'Bloquear', risk: 'muda', domain: 'clinica' },
    liberar_horario: { yes: 'Liberar', risk: 'muda', domain: 'clinica' },
    mudar_fase: { yes: 'Mudar a fase', risk: 'muda', domain: 'clinica' },
    marcar_retorno: { yes: 'Marcar retorno', risk: 'muda', domain: 'clinica' },
    finalizar_tratamento: { yes: 'Finalizar', risk: 'muda', domain: 'clinica' },
    cancelar_tratamento: { yes: 'Cancelar o plano', risk: 'alto', domain: 'clinica' },
    registrar_pagamento: { yes: 'Dar baixa', risk: 'muda', domain: 'clinica' },
    recado_para_equipe: { yes: 'Mandar recado', risk: 'muda', domain: 'clinica' },
    mover_no_funil: { yes: 'Mover', risk: 'muda', domain: 'clinica' },
    dar_baixa: { yes: 'Dar baixa', risk: 'muda', domain: 'clinica' },
    lembrar_pacientes: { yes: 'Lembrar', risk: 'muda', domain: 'clinica' },
    cadastrar_paciente: { yes: 'Cadastrar', risk: 'muda', domain: 'clinica' },
    editar_paciente: { yes: 'Salvar', risk: 'muda', domain: 'clinica' },
    responder_video: { yes: 'Responder', risk: 'muda', domain: 'clinica' },
    rascunho_de_ficha: { yes: 'Criar rascunho', risk: 'muda', domain: 'clinica' },
    rascunho_de_plano: { yes: 'Criar rascunho', risk: 'muda', domain: 'clinica' },
    comentar_diario: { yes: 'Enviar comentário', risk: 'muda', domain: 'clinica' },
    andar_pedido: { yes: 'Confirmar', risk: 'muda', domain: 'loja' },
    marcar_presenca: { yes: 'Marcar', risk: 'muda', domain: 'negocio' },
    anotar_servico_profissional: { yes: 'Anotar', risk: 'muda', domain: 'pessoal' },
    abrir_fechar_loja: { yes: 'Confirmar', risk: 'muda', domain: 'loja' },
    mudar_produto: { yes: 'Salvar', risk: 'muda', domain: 'loja' },
    cadastrar_produto: { yes: 'Cadastrar', risk: 'muda', domain: 'loja' },
    apagar_objetivo: { yes: 'Apagar', risk: 'alto', domain: 'pessoal' },
    apagar_receita: { yes: 'Apagar', risk: 'muda', domain: 'pessoal' },
    apagar_da_agenda: { yes: 'Tirar da agenda', risk: 'muda', domain: 'pessoal' },
    remarcar_na_agenda: { yes: 'Remarcar', risk: 'muda', domain: 'pessoal' },
    mover_para_lixeira: { yes: 'Mandar para a lixeira', risk: 'muda', domain: 'pessoal' },
    fazer_pedido: { yes: 'Fazer o pedido', risk: 'muda', domain: 'pessoal' },
    // Envio em lote (T7.41): mais de 3 pessoas sobe para alto pelo resumo; a fala prepara, o toque começa.
    preparar_envio: { yes: 'Enviar', risk: 'muda', domain: 'clinica' },
    preparar_envio_loja: { yes: 'Enviar', risk: 'muda', domain: 'loja' },
    preparar_envio_pessoal: { yes: 'Enviar', risk: 'muda', domain: 'pessoal' },
    pedir_a_lugares: { yes: 'Pedir', risk: 'muda', domain: 'pessoal' },
};
const fold = (text) => text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const YES = /^(sim|s|isso|isso mesmo|pode|pode sim|pode ser|pode fazer|pode mandar|pode marcar|confirmo|confirma|confirmado|claro|claro que sim|ok|okay|beleza|blz|manda|manda ver|faz|faca|fechado|certo|correto|perfeito|exato|positivo|vai|bora|uhum|aham|yes)( (sim|por favor|obrigad[oa]|pode|isso|ok|valeu|beleza|ai|la|marca|marcar|manda|mandar|faz|fazer|confirma|confirmo|segue))*$/;
const NO = /\b(nao|n|nem|espera|cancela|deixa|melhor nao|para|muda|troca|errado)\b/;
/**
 * A fala da pessoa é um sim, curto e sem condição? "Sim", "pode marcar", "isso, obrigado".
 * Qualquer "não", "espera", "muda" ou frase longa não é sim: o Secretário pergunta de novo.
 */
export function isAffirmative(said) {
    if (!said)
        return false;
    const text = fold(said);
    if (!text || text.length > 40 || NO.test(text))
        return false;
    return YES.test(text);
}
